// @flow

import * as React from 'react';

import clsx from 'clsx';
import memoize from 'memoize-one';
import { toUnicode } from 'punycode.js';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';
import { compose } from 'redux';

import { mapURLSearchParamsToQuery, serializeQuery } from '@gandi/app-runner';

import type { TranslatorProps } from '@gandi/translate';
import { Svg, Base, Text, Checkbox, TogglePill, ToggleGroup } from '@gandi/ui';
import { Select } from '@gandi/ui-select';

import { withRouter } from 'src/helpers/withRouter';
import type { WithRouterProps } from 'src/helpers/withRouter';

import type { SelectOption } from '../../../Overview/components/Products/components/types';
import type { FullState } from '../../../types';
import { change } from '../../modules/actions/filters';
import { loadSuggestions } from '../../modules/actions/suggestions';
import { fetchAllTLDs } from '../../modules/actions/tlds';

import { selectFiltersValues } from '../../modules/selectors/filters';
import {
  selectCountUnavailable,
  selectCountPremium,
} from '../../modules/selectors/results';
import { selectSearchValue } from '../../modules/selectors/search';
import { selectAllTLDs } from '../../modules/selectors/tlds';

import styles from './Filters.scss';

type SelectOptions = Array<SelectOption<string>>;

export type Query = {
  search?: string,
  options?: '0' | '1',
  bulk?: '0' | '1',
};

export type OwnProps = {
  translator: TranslatorProps,
};

type StateProps = {
  hasSearch: boolean,
  values: {
    sales: 'show' | 'hide' | 'show-only',
    premiums: 'show' | 'hide' | 'show-only',
    unavailable: 'show' | 'hide' | 'show-only',
    semantic: 'semantic' | 'exact',
    tlds: string,
    phases: string,
    ...
  },
  unavailableCount: number,
  allTLDs: Array<string>,
};

type DispatchProps = {
  onChange: (arg: { name: string, value: string }) => any,
  loadSuggestions: () => any,
  fetchAllTLDs: () => Promise<any>,
};

export type Props = {
  ...WithRouterProps<void, Query>,
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
};

export type State = {
  mounted: boolean,
  TLDsFetched: boolean,
  ...
};

function mapTLDsForMultiSelect(tld: string): SelectOption<string> {
  return {
    label: toUnicode(`.${tld}`),
    value: tld,
  };
}

const filterOption = (
  option: { label: string, value: string, data: any, ... },
  rawInput: string,
) => {
  const input = rawInput.replace('.', '');
  return createFilter({
    matchFrom: 'start',
    stringify: ({ value }) => value,
  })(option, input);
};

const getTLDValues = memoize(
  (__: TranslatorProps['__'], tlds: Array<string>, value: string) => {
    const items = tlds.map(mapTLDsForMultiSelect);
    const values = value.split(',');

    return {
      items,
      value: items.filter(item => values.indexOf(item.value) > -1),
    };
  },
);

type BaseProps = React.ElementConfig<typeof Base>;
const Label = (props: BaseProps) => (
  <Base
    weight="semibold"
    tagName="label"
    marginRight="xsmall"
    marginBottom="xsmall"
    transform="uppercase"
    {...props}
  />
);

const blockStyle = {
  textAlign: 'left',
};

const chevronStyle = {
  height: '10px',
  width: 'auto',
};

class Filters extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      mounted: false,
      TLDsFetched: props.allTLDs.length > 0,
    };
  }

  componentDidMount() {
    this.setState({
      mounted: true,
    });
  }

  // This screen is subject to performance issues.
  // Strictly control which props change should trigger re-rendering
  // Most props change should not trigger re-render when the menu is closed.
  shouldComponentUpdate(nextProps: Props, nextState: State): boolean {
    if (this.props.location !== nextProps.location) {
      return true;
    }

    if (this.state.TLDsFetched !== nextState.TLDsFetched) {
      return true;
    }

    if (this.state.mounted !== nextState.mounted) {
      return true;
    }

    const { options } = mapURLSearchParamsToQuery(this.props.searchParams);

    if (options !== '1') {
      return false;
    }

    if (this.props.values !== nextProps.values) {
      return true;
    }

    return this.props.unavailableCount !== nextProps.unavailableCount;
  }

  // Semantic filter
  // TLDs filter
  // are the only ones triggerring a new search.
  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.hasSearch) {
      if (
        prevProps.values.semantic !== this.props.values.semantic ||
        prevProps.values.phases !== this.props.values.phases ||
        prevProps.values.sales !== this.props.values.sales
      ) {
        this.props.loadSuggestions();
      }
    }

    const { options } = mapURLSearchParamsToQuery(this.props.searchParams);
    const { options: prevOptions } = mapURLSearchParamsToQuery(
      prevProps.searchParams,
    );

    if (
      options !== prevOptions ||
      (this.state.mounted !== prevState.mounted && options === '1')
    ) {
      this.fetchAllTLDs();
    }
  }

  handleTLDBlur: () => void = () => {
    this.props.loadSuggestions();
  };

  async fetchAllTLDs() {
    const { options } = mapURLSearchParamsToQuery(this.props.searchParams);
    const opened = options === '1';
    const shouldFetchTLDs = opened && !this.state.TLDsFetched;
    if (shouldFetchTLDs) {
      await this.props.fetchAllTLDs();
      this.setState({ TLDsFetched: true });
    }
  }

  handleChange: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    const target = evt.target;
    const name = target.name;
    const value = target.value;

    this.props.onChange({ name, value });
  };

  handleTLDChange: (values: SelectOptions) => void = (
    values: SelectOptions,
  ) => {
    const value = values
      ? values.map(current => current.value).join(',')
      : 'suggested';

    this.props.onChange({ name: 'tlds', value });
  };

  handlePhaseChange: (evt: SyntheticInputEvent<>) => void = (
    evt: SyntheticInputEvent<>,
  ) => {
    evt.preventDefault();

    const { values } = this.props;

    const target = evt.target;
    const value = target.value;

    const newValues = ['golive', 'landrush', 'sunrise'].reduce(
      (acc, current) => {
        // $FlowIgnore[prop-missing]
        acc[current] =
          value === current
            ? !(values.phases.indexOf(current) > -1)
            : values.phases.indexOf(current) > -1;
        return acc;
      },
      {},
    );

    const filterValue = Object.keys(newValues)
      .filter(key => newValues[key])
      .join(',');

    this.props.onChange({ name: 'phases', value: filterValue });
  };

  renderSalesFilter(): React.Node {
    const {
      translator: { __ },
    } = this.props;

    return (
      <Base padding="xsmall" className={styles.filter}>
        <Label htmlFor="sales">{__('Sales')}</Label>
        <ToggleGroup
          id="sales"
          type="radio"
          name="sales"
          value={this.props.values.sales}
          onChange={this.handleChange}
        >
          <TogglePill value="show">{__('Show')}</TogglePill>
          <TogglePill value="show-only">{__('Show Only')}</TogglePill>
          <TogglePill value="hide">{__('Hide')}</TogglePill>
        </ToggleGroup>
      </Base>
    );
  }

  renderSemanticFilter(): React.Node {
    const {
      translator: { __ },
    } = this.props;

    return (
      <Base padding="xsmall" className={styles.filter}>
        <Label htmlFor="semantic">{__('Search Terms')}</Label>
        <ToggleGroup
          id="semantic"
          type="radio"
          name="semantic"
          value={this.props.values.semantic}
          onChange={this.handleChange}
        >
          <TogglePill value="semantic">{__('Semantic')}</TogglePill>
          <TogglePill value="exact">{__('Exact Terms')}</TogglePill>
        </ToggleGroup>
      </Base>
    );
  }

  renderUnavailableFilter(): React.Node {
    const {
      translator: { __ },
      hasSearch,
      unavailableCount,
    } = this.props;

    const unavailableCountHTML = <Text color="light">{unavailableCount}</Text>;

    const unavailableCountLabel = !hasSearch
      ? __('Unavailable', { withHTML: true })
      : __('Unavailable %(unavailableCountHTML)s', {
          withHTML: true,
          unavailableCountHTML,
          escapeParameters: false,
        });

    return (
      <Base padding="xsmall" className={styles.filter}>
        <Label
          htmlFor="unavailable"
          dangerouslySetInnerHTML={unavailableCountLabel}
        />
        <ToggleGroup
          id="unavailable"
          name="unavailable"
          type="radio"
          value={this.props.values.unavailable}
          onChange={this.handleChange}
        >
          <TogglePill value="hide">{__('Hide')}</TogglePill>
          <TogglePill value="show">{__('Show')}</TogglePill>
        </ToggleGroup>
      </Base>
    );
  }

  renderTLDFilter(): null | React.Node {
    const {
      translator: { __ },
    } = this.props;

    if (!this.state.mounted) {
      return null;
    }

    const TLDCountHTML = <Text color="light">{this.props.allTLDs.length}</Text>;

    const TLDCountLabel =
      this.props.allTLDs.length > 0
        ? __('Extensions %(TLDCountHTML)s', {
            withHTML: true,
            TLDCountHTML,
            escapeParameters: false,
          })
        : __('Extensions', { withHTML: true });

    const { items, value } = getTLDValues(
      __,
      this.props.allTLDs,
      this.props.values.tlds,
    );

    const onBlur =
      this.props.hasSearch && this.state.TLDsFetched
        ? this.handleTLDBlur
        : undefined;

    return (
      <Base
        padding="xsmall"
        separatorTop
        flexGrow1
        backgroundColor="surface"
        style={blockStyle}
        flex
        flexCol
        flexWrap
        flexJustifyBetween
      >
        <Label htmlFor="tld" dangerouslySetInnerHTML={TLDCountLabel} />
        <Select
          name="tld"
          inputId="tld"
          variant="small"
          options={items}
          value={value}
          onChange={this.handleTLDChange}
          isDisabled={!this.props.allTLDs.length}
          filterOption={filterOption}
          onBlur={onBlur}
          isMulti
          translator={this.props.translator}
        />
      </Base>
    );
  }

  renderPhaseFilter(): React.Node {
    const { values } = this.props;
    const {
      translator: { __ },
    } = this.props;

    return (
      <Base
        padding="xsmall"
        separatorTop
        flexGrow1
        backgroundColor="surface"
        style={blockStyle}
        flex
        flexCol
        flexWrap
        flexJustifyBetween
      >
        <Label htmlFor="phases">{__('Phases')}</Label>
        <Base padding="none" flex flexCol flexJustifyBetween>
          <Checkbox
            onChange={this.handlePhaseChange}
            className={styles.checkbox}
            checked={values.phases.indexOf('golive') > -1}
            name="phases"
            value="golive"
            id="golive"
            key={`phases-golive-${
              values.phases.indexOf('golive') > -1 ? 'checked' : 'unchecked'
            }`}
          >
            {__('GoLive')}
          </Checkbox>
          <Checkbox
            onChange={this.handlePhaseChange}
            className={styles.checkbox}
            checked={values.phases.indexOf('landrush') > -1}
            name="phases"
            value="landrush"
            id="landrush"
            key={`phases-landrush-${
              values.phases.indexOf('landrush') > -1 ? 'checked' : 'unchecked'
            }`}
          >
            {__('Landrush')}
          </Checkbox>
          <Checkbox
            onChange={this.handlePhaseChange}
            className={styles.checkbox}
            checked={values.phases.indexOf('sunrise') > -1}
            name="phases"
            value="sunrise"
            id="sunrise"
            key={`phases-sunrise-${
              values.phases.indexOf('sunrise') > -1 ? 'checked' : 'unchecked'
            }`}
          >
            {__('Sunrise')}
          </Checkbox>
        </Base>
      </Base>
    );
  }

  render(): React.Node {
    const {
      translator: { __ },
    } = this.props;

    const { options, ...query } = mapURLSearchParamsToQuery(
      this.props.searchParams,
    );
    const opened = options === '1';
    const className = clsx(styles.container, {
      [styles.hidden]: !opened,
    });

    const SVGName = opened ? 'chevronTop' : 'chevronBottom';

    if (!opened) {
      query.options = '1';
    }

    const toggleTo = {
      search: serializeQuery(query),
    };

    return (
      <Base aria-hidden className={styles.root}>
        <Link to={toggleTo} className={styles.toggle}>
          {__('Options')}{' '}
          <Svg theme="green" name={SVGName} style={chevronStyle} />
        </Link>
        <div className={className} style={blockStyle}>
          <Base
            padding="xsmall"
            backgroundColor="surface"
            flex
            flexWrap
            flexJustifyBetween
          >
            {this.renderSemanticFilter()}
            {this.renderUnavailableFilter()}
            {this.renderSalesFilter()}
          </Base>
          {this.renderTLDFilter()}
          {this.renderPhaseFilter()}
        </div>
      </Base>
    );
  }
}

// $FlowFixMe `filters` reducer has weaker typing.
const mapStateToProps = (state: FullState): Props => ({
  hasSearch: !!selectSearchValue(state),
  // $FlowFixMe[incompatible-return]
  values: selectFiltersValues(state),
  unavailableCount: selectCountUnavailable(state),
  premiumCount: selectCountPremium(state),
  allTLDs: selectAllTLDs(state),
});

const mapDispatchToProps = {
  onChange: change,
  loadSuggestions,
  fetchAllTLDs,
};

// $FlowFixMe[signature-verification-failure]
export default compose(
  // $FlowFixMe[incompatible-call]
  connect<Props, {}, StateProps, DispatchProps, _, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRouter,
)(Filters);
